import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useRequireAuth, getCurrentUser, logout } from "@/lib/auth";
import {
  Home,
  Gamepad2,
  Map,
  ClipboardList,
  Sparkles,
  Store,
  ChefHat,
  Images,
  Wallet,
  Compass,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
} from "lucide-react";

const NAV = [
  { href: "/dashboard", icon: Home, label: "Vue d'ensemble", testid: "nav-overview", end: true },
  { href: "/dashboard/sessions", icon: Gamepad2, label: "Sessions live", testid: "nav-sessions" },
  { href: "/dashboard/parcours", icon: Map, label: "Parcours", testid: "nav-parcours" },
  { href: "/dashboard/reservations", icon: ClipboardList, label: "Réservations", testid: "nav-reservations" },
  { href: "/dashboard/ia-builder", icon: Sparkles, label: "Builder IA", testid: "nav-ia-builder" },
  { href: "/dashboard/partenaires", icon: Store, label: "Partenaires", testid: "nav-partenaires" },
  { href: "/dashboard/produits", icon: ChefHat, label: "Catalogue box", testid: "nav-produits" },
  { href: "/dashboard/communaute", icon: Images, label: "Communauté", testid: "nav-communaute" },
  { href: "/dashboard/finances", icon: Wallet, label: "Finances", testid: "nav-finances" },
];

export default function DashboardLayout() {
  useRequireAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setUser(getCurrentUser());
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div
      className="min-h-screen bg-noir text-parchemin flex bg-grain"
      data-testid="dashboard-layout"
    >
      {/* Mobile top bar */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 flex items-center justify-between px-4 bg-noir/90 backdrop-blur-md border-b border-or/20"
        data-testid="dashboard-mobile-topbar"
      >
        <Link to="/dashboard" className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-or" />
          <span className="font-display text-parchemin text-base tracking-wide">
            Picnic<span className="text-or">·</span>Game
          </span>
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 border border-or/30 text-or rounded-sm"
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          <Menu className="w-4 h-4" />
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed md:static top-0 bottom-0 left-0 z-30
          bg-noir-100 border-r border-or/15 flex flex-col
          transition-all duration-300 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
          pt-14 md:pt-0
        `}
        style={{ width: collapsed ? 68 : 232 }}
        data-testid="dashboard-sidebar"
      >
        {/* Header sidebar */}
        <div className="hidden md:flex px-4 h-16 border-b border-or/15 items-center justify-between">
          {!collapsed && (
            <Link
              to="/dashboard"
              className="flex items-center gap-2 group"
              data-testid="sidebar-logo"
            >
              <Compass className="w-4 h-4 text-or group-hover:rotate-45 transition-transform duration-500" />
              <span className="font-display text-parchemin text-base tracking-wide">
                Picnic<span className="text-or">·</span>Game
              </span>
            </Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-or/60 hover:text-or transition p-1 rounded-sm hover:bg-or/5"
            data-testid="sidebar-toggle"
            aria-label={collapsed ? "Étendre" : "Réduire"}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Section Live */}
        <div className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2" : "px-3"}`}>
          {!collapsed && (
            <div className="mb-3 px-2 flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full bg-vert-50 animate-glow-pulse"
                aria-hidden="true"
              />
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-vert-50">
                Journal · Live
              </span>
            </div>
          )}
          <nav className="flex flex-col gap-0.5">
            {NAV.map((n) => {
              const active = n.end
                ? location.pathname === n.href
                : location.pathname === n.href ||
                  location.pathname.startsWith(n.href + "/");
              const Icon = n.icon;
              return (
                <Link
                  key={n.href}
                  to={n.href}
                  data-testid={n.testid}
                  title={collapsed ? n.label : undefined}
                  className={`
                    group flex items-center gap-3 rounded-sm text-sm transition-all duration-200
                    ${collapsed ? "px-2 py-2 justify-center" : "px-3 py-2"}
                    ${
                      active
                        ? "bg-or/10 text-or border-l-2 border-or -ml-[2px] pl-[calc(0.75rem-2px)]"
                        : "text-parchemin/70 hover:text-or hover:bg-or/5"
                    }
                  `}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && (
                    <span className="tracking-wide truncate">{n.label}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Utilisateur */}
        {user && (
          <div
            className={`border-t border-or/15 py-4 ${collapsed ? "px-2" : "px-4"}`}
          >
            {!collapsed && (
              <>
                <div
                  className="text-sm text-parchemin truncate font-medium"
                  data-testid="user-prenom"
                >
                  {user.prenom || "Admin"}
                </div>
                <div
                  className="text-[10px] font-mono text-parchemin/40 truncate mt-0.5"
                  data-testid="user-email"
                >
                  {user.email}
                </div>
              </>
            )}
            <button
              onClick={() => logout(navigate)}
              data-testid="logout-btn"
              className={`
                mt-3 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest
                text-parchemin/50 hover:text-oxyde transition
                ${collapsed ? "justify-center w-full" : ""}
              `}
              title={collapsed ? "Déconnexion" : undefined}
            >
              <LogOut className="w-3 h-3" />
              {!collapsed && <span>Déconnexion</span>}
            </button>
          </div>
        )}
      </aside>

      {/* Overlay mobile */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-20 bg-noir/70 backdrop-blur-sm"
          aria-hidden="true"
        />
      )}

      {/* Main content */}
      <main
        className="flex-1 overflow-y-auto min-w-0 pt-14 md:pt-0"
        data-testid="dashboard-main"
      >
        <Outlet />
      </main>
    </div>
  );
}
